import React from 'react';
import PictureAsPdfIcon from '@material-ui/icons/PictureAsPdf';
import ImageIcon from '@material-ui/icons/Image';
import MovieIcon from '@material-ui/icons/Movie';
import DescriptionIcon from '@material-ui/icons/Description';
import GridOnIcon from '@material-ui/icons/GridOn';
import AttachFileIcon from '@material-ui/icons/AttachFile';
import { getOrgDocuments, uploadOrgDocument, updateOrgDocument, deleteOrgDocument, downloadOrgDocument } from '../api/api';
import FileUpload from '../Common/FileUpload';
import './OrgDocuments.css';

const DOC_IMAGE_LIMIT = 10 * 1024 * 1024; // 10MB
const VIDEO_LIMIT = 50 * 1024 * 1024; // 50MB
const VIDEO_EXTENSIONS = ['mp4', 'mov', 'avi', 'wmv', 'mkv', 'webm'];
const ALLOWED_EXTENSIONS = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'jpg', 'jpeg', 'png', 'gif', 'svg', ...VIDEO_EXTENSIONS];

class OrgDocuments extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            documents: [],
            loading: false,
            saving: false,
            error: null,
            message: null,
            uploading: false,
            editingId: null,
            editingTitle: '',
            selectedFile: null,
        };
        this._msgTimer = null;
    }

    componentDidMount() {
        this.fetchDocuments();
    }

    componentDidUpdate(prevProps) {
        // Re-fetch when org changes
        if (prevProps.orgId !== this.props.orgId) {
            this.fetchDocuments();
        }
    }

    componentWillUnmount() {
        if (this._msgTimer) clearTimeout(this._msgTimer);
    }

    fetchDocuments = async () => {
        const { orgId } = this.props;
        if (!orgId) return;

        this.setState({ loading: true, error: null });
        try {
            const data = await getOrgDocuments(orgId);
            if (data.status === 'success') {
                this.setState({ documents: data.documents || [], error: null });
            } else {
                this.setState({ error: data.message || 'Failed to fetch documents' });
            }
        } catch (error) {
            this.setState({ error: error.message || 'Error fetching documents' });
        } finally {
            this.setState({ loading: false });
        }
    };

    handleFileSelect = (files) => {
        // Called by FileUpload component
        if (!files || files.length === 0) {
            this.setState({ selectedFile: null, error: null });
            return;
        }
        const file = files[0];
        const ext = file.name.split('.').pop()?.toLowerCase();

            // Validate extension
            if (!ALLOWED_EXTENSIONS.includes(ext || '')) {
                this.setState({
                    error: `File type not allowed. Allowed: ${ALLOWED_EXTENSIONS.join(', ')}`
                });
                return;
            }

            // Validate size
            const limit = VIDEO_EXTENSIONS.includes(ext) ? VIDEO_LIMIT : DOC_IMAGE_LIMIT;
            const limitMB = limit / (1024 * 1024);
            if (file.size > limit) {
                this.setState({
                    error: `File exceeds ${limitMB}MB limit. Size: ${(file.size / (1024 * 1024)).toFixed(2)}MB`
                });
                return;
            }

            this.setState({ selectedFile: file, error: null });
    };

    handleFilesUpdate = (updaterOrFiles) => {
        const currentFiles = this.state.selectedFile ? [this.state.selectedFile] : [];
        const nextFiles = typeof updaterOrFiles === 'function'
            ? updaterOrFiles(currentFiles)
            : updaterOrFiles;
        // Take only the last file so dropping a new file replaces the existing one
        this.handleFileSelect(nextFiles.length > 0 ? [nextFiles[nextFiles.length - 1]] : []);
    };

    handleUpload = async () => {
        const { selectedFile } = this.state;
        const { orgId } = this.props;

        if (!selectedFile) {
            this.setState({ error: 'Please select a file first' });
            return;
        }

        this.setState({ uploading: true, error: null, message: null });
        try {
            const formData = new FormData();
            formData.append('customer_id', orgId);
            formData.append('title', selectedFile.name);
            formData.append('file', selectedFile);

            const data = await uploadOrgDocument(formData);
            if (data.status === 'success') {
                this.setState({
                    message: 'Document uploaded successfully',
                    selectedFile: null,
                    error: null
                });
                if (this._msgTimer) clearTimeout(this._msgTimer);
                this._msgTimer = setTimeout(() => this.setState({ message: null }), 4000);
                // Refresh list
                await this.fetchDocuments();
            } else {
                this.setState({ error: data.message || 'Upload failed' });
            }
        } catch (error) {
            this.setState({ error: error.response?.data?.message || error.message || 'Upload failed' });
        } finally {
            this.setState({ uploading: false });
        }
    };

    handleEditStart = (doc) => {
        this.setState({ editingId: doc.id, editingTitle: doc.title });
    };

    handleEditCancel = () => {
        this.setState({ editingId: null, editingTitle: '' });
    };

    handleEditSave = async (docId) => {
        const { editingTitle } = this.state;
        if (!editingTitle.trim()) {
            this.setState({ error: 'Title cannot be empty' });
            return;
        }

        this.setState({ saving: true, error: null });
        try {
            const data = await updateOrgDocument(docId, { title: editingTitle });
            if (data.status === 'success') {
                this.setState({ message: 'Document updated successfully', editingId: null });
                if (this._msgTimer) clearTimeout(this._msgTimer);
                this._msgTimer = setTimeout(() => this.setState({ message: null }), 4000);
                await this.fetchDocuments();
            } else {
                this.setState({ error: data.message || 'Update failed' });
            }
        } catch (error) {
            this.setState({ error: error.message || 'Update failed' });
        } finally {
            this.setState({ saving: false });
        }
    };

    handleDownload = async (doc) => {
        try {
            await downloadOrgDocument(doc.id, doc.title);
        } catch (error) {
            this.setState({ error: 'Download failed' });
            setTimeout(() => this.setState({ error: null }), 5000);
        }
    };

    handleDelete = async (docId) => {
        if (!window.confirm('Are you sure you want to delete this document?')) {
            return;
        }

        this.setState({ loading: true, error: null });
        try {
            const data = await deleteOrgDocument(docId);
            if (data.status === 'success') {
                this.setState({ message: 'Document deleted successfully' });
                if (this._msgTimer) clearTimeout(this._msgTimer);
                this._msgTimer = setTimeout(() => this.setState({ message: null }), 4000);
                await this.fetchDocuments();
            } else {
                this.setState({ error: data.message || 'Delete failed' });
            }
        } catch (error) {
            this.setState({ error: error.message || 'Delete failed' });
        } finally {
            this.setState({ loading: false });
        }
    };

    formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
    };

    formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString() + ' ' + new Date(dateString).toLocaleTimeString();
    };

    getFileIcon = (fileType) => {
        if (['jpg', 'jpeg', 'png', 'gif', 'svg'].includes(fileType)) return <ImageIcon className="doc-icon" />;
        if (['mp4', 'mov', 'avi', 'wmv', 'mkv', 'webm'].includes(fileType)) return <MovieIcon className="doc-icon" />;
        if (fileType === 'pdf') return <PictureAsPdfIcon className="doc-icon" />;
        if (['doc', 'docx'].includes(fileType)) return <DescriptionIcon className="doc-icon" />;
        if (['xls', 'xlsx'].includes(fileType)) return <GridOnIcon className="doc-icon" />;
        return <AttachFileIcon className="doc-icon" />;
    };

    render() {
        const { documents, loading, saving, error, message, uploading, editingId, editingTitle, selectedFile } = this.state;

        return (
            <div className="org-documents-section">
                <h3>Organization Documents</h3>

                {error && <div className="alert alert-danger">{error}</div>}
                {message && <div className="alert alert-success">{message}</div>}

                <div className="documents-upload-section">
                    <h5>Upload New Document</h5>
                    <p className="text-muted small">
                        Allowed: PDF, DOC, DOCX, XLS, XLSX, images (JPG, PNG, GIF, SVG), video (MP4, MOV, AVI, WMV, MKV, WEBM)
                        <br />
                        Limits: 10MB for documents/images, 50MB for video.
                        <br />
                    </p>
                    <FileUpload setFiles={this.handleFilesUpdate} files={selectedFile ? [selectedFile] : []} />
                    {selectedFile && (
                        <div className="mt-2">
                            <small>Selected: {selectedFile.name} ({this.formatFileSize(selectedFile.size)})</small>
                        </div>
                    )}
                    <button
                        type="button"
                        className="btn btn-primary mt-2"
                        onClick={this.handleUpload}
                        disabled={uploading || !selectedFile || loading}
                    >
                        {uploading ? 'Uploading...' : 'Upload'}
                    </button>
                </div>

                <div className="documents-list-section mt-4">
                    <h5>Documents ({documents.length})</h5>
                    {loading ? (
                        <p>Loading...</p>
                    ) : documents.length === 0 ? (
                        <p className="text-muted">No documents uploaded yet</p>
                    ) : (
                        <table className="table table-striped table-sm">
                            <thead>
                                <tr>
                                    <th>Type</th>
                                    <th>Title</th>
                                    <th>Size</th>
                                    <th>Uploaded</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {documents.map((doc) => (
                                    <tr key={doc.id}>
                                        <td>{this.getFileIcon(doc.file_type)}</td>
                                        <td>
                                            {editingId === doc.id ? (
                                                <input
                                                    type="text"
                                                    value={editingTitle}
                                                    onChange={(e) => this.setState({ editingTitle: e.target.value })}
                                                    className="form-control form-control-sm"
                                                />
                                            ) : (
                                                <span>{doc.title}</span>
                                            )}
                                        </td>
                                        <td>{this.formatFileSize(doc.file_size)}</td>
                                        <td>{this.formatDate(doc.date_uploaded)}</td>
                                        <td>
                                            {editingId === doc.id ? (
                                                <>
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm btn-success me-1"
                                                        onClick={() => this.handleEditSave(doc.id)}
                                                        disabled={saving}
                                                    >
                                                        {saving ? 'Saving...' : 'Save'}
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm btn-secondary"
                                                        onClick={this.handleEditCancel}
                                                    >
                                                        Cancel
                                                    </button>
                                                </>
                                            ) : (
                                                <>
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm btn-link"
                                                        onClick={() => this.handleEditStart(doc)}
                                                    >
                                                        Edit
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm btn-secondary"
                                                        onClick={() => this.handleDownload(doc)}
                                                    >
                                                        Download
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm btn-danger"
                                                        onClick={() => this.handleDelete(doc.id)}
                                                    >
                                                        Delete
                                                    </button>
                                                </>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        );
    }
}

export default OrgDocuments;
